export type ClassNamingScheme = 'nigerian_standard' | 'international_grade';

// Class tiers, 0-indexed to match the `classes.tier` column.
// Blueprint Section 4: four fixed templates, Highschool = JSS+SS combined.
export const TIERS = ['kindergarten', 'nursery', 'primary', 'highschool'] as const;
export type Tier = typeof TIERS[number];
export const TIER_INDEX: Record<Tier, number> = { kindergarten: 0, nursery: 1, primary: 2, highschool: 3 };

export interface ClassTemplateEntry {
  name: string;
  tier: Tier;
  // Position within the tier — mirrors today's grade_level-as-position
  // convention, just scoped to one tier instead of the whole school.
  tier_position: number;
}

// ─── Nigeria class templates ────────────────────────────────────────────
// Two label sets for the same four tiers / same year-count per tier, per
// the naming-scheme sub-question asked at admin verification. Early-years
// labels (Kindergarten/Nursery) stay conventional under both schemes —
// real Nigerian international-curriculum schools still say "Nursery",
// they just switch to "Grade N" once numbered schooling starts, same as
// standard schools switch to "Primary N"/"JSS N"/"SS N".

const NIGERIA_TEMPLATES_STANDARD: ClassTemplateEntry[] = [
  { name: 'Kindergarten 1', tier: 'kindergarten', tier_position: 1 },
  { name: 'Kindergarten 2', tier: 'kindergarten', tier_position: 2 },
  { name: 'Nursery 1', tier: 'nursery', tier_position: 1 },
  { name: 'Nursery 2', tier: 'nursery', tier_position: 2 },
  { name: 'Primary 1', tier: 'primary', tier_position: 1 },
  { name: 'Primary 2', tier: 'primary', tier_position: 2 },
  { name: 'Primary 3', tier: 'primary', tier_position: 3 },
  { name: 'Primary 4', tier: 'primary', tier_position: 4 },
  { name: 'Primary 5', tier: 'primary', tier_position: 5 },
  { name: 'Primary 6', tier: 'primary', tier_position: 6 },
  { name: 'JSS 1', tier: 'highschool', tier_position: 1 },
  { name: 'JSS 2', tier: 'highschool', tier_position: 2 },
  { name: 'JSS 3', tier: 'highschool', tier_position: 3 },
  { name: 'SS 1', tier: 'highschool', tier_position: 4 },
  { name: 'SS 2', tier: 'highschool', tier_position: 5 },
  { name: 'SS 3', tier: 'highschool', tier_position: 6 },
];

const NIGERIA_TEMPLATES_INTERNATIONAL: ClassTemplateEntry[] = [
  { name: 'Pre-KG 1', tier: 'kindergarten', tier_position: 1 },
  { name: 'Pre-KG 2', tier: 'kindergarten', tier_position: 2 },
  { name: 'Nursery 1', tier: 'nursery', tier_position: 1 },
  { name: 'Nursery 2', tier: 'nursery', tier_position: 2 },
  { name: 'Grade 1', tier: 'primary', tier_position: 1 },
  { name: 'Grade 2', tier: 'primary', tier_position: 2 },
  { name: 'Grade 3', tier: 'primary', tier_position: 3 },
  { name: 'Grade 4', tier: 'primary', tier_position: 4 },
  { name: 'Grade 5', tier: 'primary', tier_position: 5 },
  { name: 'Grade 6', tier: 'primary', tier_position: 6 },
  { name: 'Grade 7', tier: 'highschool', tier_position: 1 },
  { name: 'Grade 8', tier: 'highschool', tier_position: 2 },
  { name: 'Grade 9', tier: 'highschool', tier_position: 3 },
  { name: 'Grade 10', tier: 'highschool', tier_position: 4 },
  { name: 'Grade 11', tier: 'highschool', tier_position: 5 },
  { name: 'Grade 12', tier: 'highschool', tier_position: 6 },
];

export function getNigeriaClassTemplates(scheme: ClassNamingScheme | null | undefined): ClassTemplateEntry[] {
  return scheme === 'international_grade' ? NIGERIA_TEMPLATES_INTERNATIONAL : NIGERIA_TEMPLATES_STANDARD;
}
